import {isNumber} from 'utils/IsNumber.js';
import {setParametersToRecord} from 'actions/index.js';
import {FILTER, DELAY, SEND_A} from 'constants/midi-cc-actions.js';
import Resolution from 'engine/Resolution.js';
import MidiKnobAction from './MidiKnobAction.js';
import MidiCC from 'midi/MidiCC.js';
import {
    PLAY,
    RECORD,
    CLEAR,
    VOLUME,
    KNOB_1,
    KNOB_2,
    KNOB_3,
    BROWSE_SOUNDS,
    CHANGE_SELECTED_TRACK,
    CHANGE_START_SLICE,
    CHANGE_END_SLICE,
    CHANGE_TRANSPOSE,
    CHANGE_VOLUME
} from 'constants/midi-cc-actions.js';

import {
    SEQUENCE_ROLL,
    PATTERN_SWITCH,
    PREVIOUS_PATTERN,
    NEXT_PATTERN,
} from 'constants/midi-actions.js';
const NUMBER_TO_RESOLUTION = {
    1: new Resolution(4, false),
    2: new Resolution(4, true),
    3: new Resolution(8, false),
    4: new Resolution(8, true),
    5: new Resolution(16, false),
    6: new Resolution(16, true),
    7: new Resolution(32, false),
    8: new Resolution(32, true),
};

let counter=0;

class SliderMidiCC {
    constructor(sequencerEngine, config, cc) {
        this.cc = cc;
        this.sequencerEngine = sequencerEngine;
        this.lastNumber = 0;
        this.ccToAction = this.convertConfig(config.cc);
        if (config.actions) {
            this.noteToAction = this.convertConfig2(config.actions);
        }

        if (config.note) {
            this.noteToAction = this.convertConfig(config.note);
        }

        if (config.ccActions) {
            this.ccToAction = this.convertConfig2(config.ccActions);
        }
        this.overwrittenCC = new Set();
        this.knob1Action = new MidiKnobAction(FILTER);
        this.knob2Action = new MidiKnobAction(DELAY);
        this.knob3Action = new MidiKnobAction(SEND_A);

        this.rollingCount = 0;
    }

    get state() {
        return this.sequencerEngine.state;
    }

    overwrite(ccNumber) {
        this.overwrittenCC.add(ccNumber);
    }

    onNoteOnEvent(e) {
        let number = e.note.number;
        if (this.noteToAction === undefined) {
            return;
        }
        let action = this.noteToAction[number];
        if (action === undefined) {
            return;
        }

        switch(action.action) {
        case SEQUENCE_ROLL:
            this.onSequenceRollOn(action.track);
            break;
        case PLAY:
            this.onPlay();
            break;
        case RECORD:
            this.onRecord();
            break;
        case CLEAR:
            this.onClear();
            break;
        case PATTERN_SWITCH:
            this.onPatternSwitch(action.track);
            break;
        case NEXT_PATTERN:
            this.onNextPattern();
            break;
        case PREVIOUS_PATTERN:
            this.onPreviousPattern();
            break;
        default:
            return true;
        }
        return true;
    }

    onNoteOffEvent(e) {
        let number = e.note.number;
        if (this.noteToAction === undefined) {
            return;
        }
        let action = this.noteToAction[number];

        if (action === undefined) {
            return;
        }

        switch(action.action) {
        case SEQUENCE_ROLL:
            this.onSequenceRollOff(action.track);
            break;
        case PATTERN_SWITCH:
            break;
        default:
            return true;
        }
        return true;
    }

    onEvent(ccNumber, value) {
        if (!this.ccToAction) {
            return;
        }
        let ccInfo = this.ccToAction[ccNumber];
        if (ccInfo === undefined || this.overwrittenCC.has(ccNumber)) {
            return;
        }


        switch (ccInfo.action) {
        case VOLUME:
            this.onVolume(ccInfo.track, value);
            break;
        case KNOB_1:
            this.onKnob1(ccInfo.track, value);
            break;
        case KNOB_2:
            this.onKnob2(ccInfo.track, value);
            break;
        case KNOB_3:
            this.onKnob3(ccInfo.track, value);
            break;
        case CHANGE_SELECTED_TRACK:
            this.changeSelectedTrack(value);
            break;
        case BROWSE_SOUNDS:
            this.browseSounds(value);
            break;
        case CHANGE_TRANSPOSE:
            this.changeTranspose(value);
            break;
        case CHANGE_START_SLICE:
            this.changeStartSlice(value);
            break;
        case CHANGE_END_SLICE:
            this.changeEndSlice(value);
            break;
        case CHANGE_VOLUME:
            this.changeVolume(value);
            break;
        }
    }

    onVolume(number, value) {
        let subSequence = this.sequencerEngine.subSequences[number];
        if (subSequence === undefined) {
            return;
        }
        subSequence.fade = value / 128;
    }

    // by default Knob 1 is filter
    onKnob1(number, value) {
        let track = this.getTrack(number);
        this.knob1Action.onChange(track, value);

        let subSequence = this.sequencerEngine.subSequences[number];
        if (subSequence === undefined) {
            return;
        }
        this.sequencerEngine.switchToSubSequence(subSequence.id);
    }

    // by default Knob 2 is delay wet/frequency
    onKnob2(number, value) {
        let track = this.getTrack(number);
        this.knob2Action.onChange(track, value);

        let subSequence = this.sequencerEngine.subSequences[number];
        if (subSequence === undefined) {
            return;
        }
        this.sequencerEngine.switchToSubSequence(subSequence.id);
    }

    // by default knob 3 is Send A
    onKnob3(number, value) {
        let track = this.getTrack(number);
        this.knob3Action.onChange(track, value);

        let subSequence = this.sequencerEngine.subSequences[number];
        if (subSequence === undefined) {
            return;
        }
        this.sequencerEngine.switchToSubSequence(subSequence.id);
    }

    convertConfig2(config) {
        let converted = {};
        for (let action in config) {
            let note = config[action];
            converted[note] = {
                action: action,
            };
        }
        return converted;
    }

    convertConfig(config) {
        let converted = {};
        for (let i in config) {
            let ccInfo = {
                track: isNumber(i) ? parseInt(i) : i
            };
            let trackConfig = config[i];
            for (let action in trackConfig) {
                let cc = trackConfig[action];
                converted[cc] = {
                    ... ccInfo,
                    action: action
                };
            }
        }

        return converted;
    }

    getTrack(number) {
        if (number === 'MASTER') {
            return undefined;
        }
        return this.sequencerEngine.tracks[number];
    }

    onSequenceRollOn(number) {
        let resolution = NUMBER_TO_RESOLUTION[number];
        if (resolution === undefined) {
            return;
        }
        this.sequencerEngine.resolution = resolution;
        this.sequencerEngine.trackRolling = true;
        this.sequencerEngine.setSequenceRolling(
            true, resolution);

        this.rollingCount++;
    }

    onSequenceRollOff(number) {
        this.rollingCount--;
        if (this.rollingCount === 0) {
            this.sequencerEngine.trackRolling = false;
            this.sequencerEngine.setSequenceRolling(
                false, undefined);
        }
    }

    onPatternSwitch(number) {
        this.sequencerEngine.switchToPattern(number);
    }

    onNextPattern() {
        this.sequencerEngine.switchToNextPattern();
    }

    onPreviousPattern() {
        this.sequencerEngine.switchToPreviousPattern();
    }

    onPlay() {
        this.sequencerEngine.play();
    }

    onClear() {
        let track = this.sequencerEngine.getSelectedMidiTrack();
        if (track) {
            track.pattern.clear();
            track.updateSteps();
        }
    }

    onRecord() {
        this.sequencerEngine.recording(!this.sequencerEngine.isRecording);
    }

    changeSelectedTrack(val) {
        let track = this.sequencerEngine.getSelectedMidiTrack();
        let trackNumber = Math.floor((val / 128) * this.sequencerEngine.tracks.length);
        this.sequencerEngine.selectTrack(trackNumber);
    }

    browseSounds(val) {
        let _counter = ++counter;
        setTimeout(() => {
            if (counter !== _counter) {
                return;
            }
            let track = this.sequencerEngine.getSelectedMidiTrack();
            if (track === undefined) {
                return;
            }
            if (Math.abs(this.lastBrowseValue - val) <= 2) {

                if (val >= 125) {
                    this.resetBrowseToZero();
                }
                // so slight movements dont fuck things up
                return;
            }
            let next = this.lastBrowseValue === undefined || val >= this.lastBrowseValue;
            this.lastBrowseValue = val;
            if (next) {
                track.auditionNext(true, true);
            } else {
                track.auditionPrevious(true, true);
            }
            if (val >= 125) {
                this.resetBrowseToZero();
            }
        }, 50);
    }

    resetBrowseToZero() {
        this.lastBrowseValue = 0;
        // find cc to send
        for (let x in this.ccToAction) {
            if (this.ccToAction[x].action === BROWSE_SOUNDS) {
                this.cc.send(parseInt(x), 0);
                return;
            }
        }
    }

    changeResolution(val) {
        let index = 1 + Math.floor((val / 128.0) * 8.0);
        this.sequencerEngine.resolution = NUMBER_TO_RESOLUTION[index];
    }

    changeStartSlice(val) {
        let ratio = val / 128.0;
        let track = this.sequencerEngine.getSelectedMidiTrack();
        if (!track) {
            return;
        }

        let keys = Object.keys(track.samplers);
        if (keys.length === 0) {
            return;
        }

        let sampler = track.samplers[keys[keys.length - 1]];
        let sliceNumber = this.getSlice(track);
        let slice = sampler.getSlice(sliceNumber);
        let newStart = ratio * slice.endTime;
        if (newStart < slice.endTime) {
            sampler.slice(sliceNumber, newStart, slice.endTime, undefined, undefined, true);
        }
    }

    getSlice(track) {
        return this.state.tracks.selectedTrackSlice[track.id] || 0;
    }

    changeEndSlice(val) {
        let ratio = val / 128.0;
        let track = this.sequencerEngine.getSelectedMidiTrack();
        if (!track) {
            return;
        }
        let keys = Object.keys(track.samplers);
        if (keys.length === 0) {
            return;
        }

        let sampler = track.samplers[keys[keys.length - 1]];
        let sliceNumber =  this.getSlice(track);
        let slice = sampler.getSlice(sliceNumber);
        let duration = sampler.getDuration();
        let newEnd  = slice.startTime + ratio * (duration - slice.startTime);
        if (newEnd > slice.startTime) {
            sampler.slice(sliceNumber, slice.startTime, newEnd, undefined, undefined, true);
        }
    }

    changeVolume(val) {
        let ratio = val / 128.0;
        let track = this.sequencerEngine.getSelectedMidiTrack();
        if (!track) {
            return;
        }

        let volume = -60 + (64) * Math.pow(ratio, 0.5);
        track.setVolume(volume);
    }

    changeTranspose(val) {
        let track = this.sequencerEngine.getSelectedMidiTrack();
        if (!track) {
            return;
        }

        let ratio = -24 + 48 * (val / 128.0)
        track.trackData.transpose = ratio;

        // set the recording transpose to this number
        /*
        this.sequencerEngine.dispatch(
            setParametersToRecord(track.trackData.transposeParameter));
            */
    }
}

export default SliderMidiCC;
